import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { SlotFieldsOff } from '@/lib/waterModel';
import type { FarmResponse } from '@/types/api';
import { useCurrentFarm } from './useCurrentFarm';

function useFarmSettingsQuery(farmIdOverride?: string | null) {
  const { farmId: currentFarmId } = useCurrentFarm();
  const farmId = farmIdOverride ?? currentFarmId;
  const query = useQuery({
    queryKey: ['farm', farmId],
    queryFn: () => api.get<{ data: FarmResponse }>(`/api/v1/farms/${farmId}`),
    enabled: !!farmId,
    staleTime: 5 * 60 * 1000,
  });
  return { ...query, farmId };
}

/**
 * Field tests this farm does not take at a slot (#371), e.g. { '3pm': ['salinity_ppt'] } for a farm
 * that measures salinity in the morning only. Empty until the farm loads: every field is expected,
 * as it always was. Shares the settings page's cache entry. Anything that isn't a slot -> list of
 * column names is ignored (the API cleans it too).
 */
export function useWaterSlotRules(farmIdOverride?: string | null): SlotFieldsOff | undefined {
  const { data } = useFarmSettingsQuery(farmIdOverride);
  const raw = data?.data?.settings?.water_fields_not_measured;
  if (!raw || typeof raw !== 'object') return undefined;
  const clean: SlotFieldsOff = {};
  for (const [slot, columns] of Object.entries(raw)) {
    if (Array.isArray(columns)) clean[slot] = columns.filter((c): c is string => typeof c === 'string');
  }
  return clean;
}

/** True once the farm's settings have loaded (or failed to): screens that pick a first field from
 *  the slot's field list wait for it, so they never pick a field the farm doesn't take. */
export function useWaterSlotRulesReady(farmIdOverride?: string | null): boolean {
  const { isFetched, isError, farmId } = useFarmSettingsQuery(farmIdOverride);
  return !farmId || isFetched || isError;
}
